import type { Contact } from '@taler/api-client'
import { contactKeys } from '@taler/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { problemDetail } from '../internal/problem.ts'

export interface UseContactsOptions {
  readonly ledgerId: () => string | undefined
  readonly enabled: () => boolean
}

export interface CreateContactInput {
  readonly name: string
  readonly notes?: string
}

export interface UseContacts {
  readonly contacts: ComputedRef<readonly Contact[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  readonly isCreating: ComputedRef<boolean>
  readonly hasCreateError: ComputedRef<boolean>
  /** The server's own reason, when it sent one - undefined for a network/unknown failure. */
  readonly createErrorDetail: ComputedRef<string | undefined>
  create(input: CreateContactInput): Promise<Contact>
  retry(): void
}

export function useContacts(options: UseContactsOptions): UseContacts {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const contactsQuery = useQuery({
    queryKey: computed(() => contactKeys.list(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.contacts.list(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  const createMutation = useMutation({
    mutationFn: (input: CreateContactInput) => api.contacts.create(options.ledgerId()!, input),
    onSuccess: () => {
      for (const key of contactKeys.invalidatedByWrite(options.ledgerId() ?? '')) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  return {
    contacts: computed(() => contactsQuery.data.value ?? []),
    isLoading: computed(() => active.value && contactsQuery.isPending.value),
    error: computed(() => contactsQuery.error.value),
    retrying: retrying(contactsQuery),
    isCreating: computed(() => createMutation.isPending.value),
    hasCreateError: computed(() => createMutation.error.value !== null),
    createErrorDetail: computed(() => problemDetail(createMutation.error.value)),
    create: (input) => createMutation.mutateAsync(input),
    retry: () => {
      void contactsQuery.refetch()
    },
  }
}
